import { useMemo } from "react";

import { BaseCheckCircleIcon, HelpIcon } from "../../../Icons";
import { Typo } from "../../../Typo";
import { useRecentLeadsTranslatedContent } from "../../OverviewTables/RecentLeadsTable/useRecentLeadsTranslatedContent";
import { LeadRawStatus } from "../types";

export const RecentLeadStatusDisplayContent = ({
  status,
}: {
  status: string;
}) => {
  const translatedContent = useRecentLeadsTranslatedContent();
  const display = useMemo(() => {
    switch (status.toLowerCase()) {
      case LeadRawStatus.Demo:
        return {
          level: 1,
          label:
            translatedContent.leadStatusTooltipLabelMapping.get(
              LeadRawStatus.Demo,
            ) || "",
          step: translatedContent.stepMapping.get(LeadRawStatus.Demo) || "",
          description:
            translatedContent.descriptionStatusMapping.get(
              LeadRawStatus.Demo,
            ) || "",
        };
      case LeadRawStatus.Lead_1:
        return {
          level: 2,
          label:
            translatedContent.leadStatusTooltipLabelMapping.get(
              LeadRawStatus.Lead_1,
            ) || "",
          step: translatedContent.stepMapping.get(LeadRawStatus.Lead_1) || "",
          description:
            translatedContent.descriptionStatusMapping.get(
              LeadRawStatus.Lead_1,
            ) || "",
        };
      case LeadRawStatus.Lead_2:
        return {
          level: 3,
          label:
            translatedContent.leadStatusTooltipLabelMapping.get(
              LeadRawStatus.Lead_2,
            ) || "",
          step: translatedContent.stepMapping.get(LeadRawStatus.Lead_2) || "",
          description:
            translatedContent.descriptionStatusMapping.get(
              LeadRawStatus.Lead_2,
            ) || "",
        };
      case LeadRawStatus.Lead_4:
        return {
          level: 4,
          label:
            translatedContent.leadStatusTooltipLabelMapping.get(
              LeadRawStatus.Lead_4,
            ) || "",
          step: translatedContent.stepMapping.get(LeadRawStatus.Lead_4) || "",
          description:
            translatedContent.descriptionStatusMapping.get(
              LeadRawStatus.Lead_4,
            ) || "",
        };
      case LeadRawStatus.Lead_5:
        return {
          level: 5,
          label:
            translatedContent.leadStatusTooltipLabelMapping.get(
              LeadRawStatus.Lead_5,
            ) || "",
          step: translatedContent.stepMapping.get(LeadRawStatus.Lead_5) || "",
          description:
            translatedContent.descriptionStatusMapping.get(
              LeadRawStatus.Lead_5,
            ) || "",
        };
      case LeadRawStatus.Approved:
        return {
          level: undefined,
          label:
            translatedContent.leadStatusTooltipLabelMapping.get(
              LeadRawStatus.Approved,
            ) || "",
          step: translatedContent.stepMapping.get(LeadRawStatus.Approved) || "",
          description: translatedContent.approvedDescription2,
        };
      default:
        return {
          level: undefined,
          label:
            translatedContent.leadStatusTooltipLabelMapping.get(
              LeadRawStatus.Unknown,
            ) || "",
          step: "",
          description:
            translatedContent.descriptionStatusMapping.get(
              LeadRawStatus.Unknown,
            ) || "",
        };
    }
  }, [status, translatedContent]);

  return (
    <div className="flex flex-grow flex-col items-start justify-between rtl:items-end">
      <RecentLeadStatusIndicator status={status} />
      <div className="mb-2 mt-4 flex w-full flex-col rtl:text-right">
        {display.level && (
          <Typo type="bodyXS" textColor="text-text-tertiary">
            {display.step}
          </Typo>
        )}
        <Typo type="labelSM">{display.label}</Typo>
      </div>
      <Typo type="bodySM">{display.description}</Typo>
    </div>
  );
};

const RecentLeadStatusIndicator = ({ status }: { status: string }) => {
  switch (status.toLowerCase()) {
    case LeadRawStatus.Demo:
      return <RecentLeadStatusLevel level={1} />;
    case LeadRawStatus.Lead_1:
      return <RecentLeadStatusLevel level={2} />;
    case LeadRawStatus.Lead_2:
      return <RecentLeadStatusLevel level={3} />;
    case LeadRawStatus.Lead_4:
      return <RecentLeadStatusLevel level={4} />;
    case LeadRawStatus.Lead_5:
      return <RecentLeadStatusLevel level={5} />;
    case LeadRawStatus.Approved:
      return <BaseCheckCircleIcon />;
    default:
      return <HelpIcon />;
  }
};

const RecentLeadStatusLevel = ({ level }: { level: number }) => {
  return (
    <div className="flex w-full flex-row items-stretch justify-center gap-1">
      {Array(5)
        .fill(0)
        .map((_, index) => (
          <div
            key={index}
            className={`h-[8px] min-w-[3px] flex-1 rounded ${index < level ? "bg-icon-brand-default" : "bg-surface-tonal-bold"}`}
          />
        ))}
    </div>
  );
};
